import React, { ReactNode, useState, useCallback, useEffect, useContext } from 'react';
import Firebase from '../firebase';
import type { ClientApplication } from '@shopify/app-bridge';
import { AppConfigContext } from './AppConfigContext';
import { ToastContext } from '../ToastContext';

type TAppConfigProvider = {
  firebase: Firebase;
  app: ClientApplication;
  children: ReactNode;
}

export const AppConfigProvider: React.FC<TAppConfigProvider> = ({ firebase, app, children }) => {
  const { dispatchToast } = useContext(ToastContext);
  const [appConfig, setAppConfig] = useState({});
  const [isFetchingConfig, setIsFetchingConfig] = useState(true);

  const updateConfigState = (configData: Record<string, any>, configId?: string) =>
    setAppConfig((prevState: Record<string, any>) => {
      let newState = {};
      newState = {
        ...prevState,
        ...(configId
          ? {
              [configId]: {
                ...prevState[configId],
                ...configData,
              },
            }
          : configData),
      };

      return newState;
    });

  const fetchAppConfig = useCallback(async () => {
    const configKeys = ["customers"];
    let configData: Record<string, any> = {
      customers: {}
    };

    try {
      for (const configKey of configKeys) {
        const configDoc = await firebase.specificConfig(configKey).get();

        if (configDoc.exists) {
          configData[configKey] = {
            ...configDoc.data(),
          };
        }
      }
    } catch (error) {
      console.error('Error fetching app config data:', error);
    } finally {
      updateConfigState(configData);
      setIsFetchingConfig(false);
    }
  }, [firebase]);

  useEffect(() => {
    if (Object.keys(firebase.userData).length > 0) {
      fetchAppConfig();
    }
  }, [firebase.userData, fetchAppConfig]);

  const saveAppConfig = useCallback(
    async (configId: string, dataToSave: Record<string, any>) => {
      try {
        await firebase.specificConfig(configId).update(dataToSave);
        updateConfigState(dataToSave, configId);
        dispatchToast("The configuration is stored successfully");
        return true;
      } catch (error) {
        console.error('Error saving app config data:', error);
        dispatchToast("Something went wrong while trying to store the config", { isError: true });
        throw error;
      }
    },
    [dispatchToast, firebase],
  );

  return (
  <AppConfigContext.Provider value={{ 
    appConfig,
    saveAppConfig,
    isFetchingConfig,
  }}>
    {children}
  </AppConfigContext.Provider>
  );
}